import { trackError } from '../../lib/analytics'
import { ChatStreamError, type ChatFailureReason, type ChatTurnStats } from './postChat'
import { CHAT_FAILURE_NOTICES } from '../../../netlify/functions/lib/project-chat-transcript'

// Client-side reporting for chat turns that failed or came back empty.
//
// Chat used to be the one agent surface with NO durable record of failure: /api/project-chat is a
// native streaming handler (not recorder-wrapped, so nothing in backend_requests), callLlmStreaming
// wrote no llm_jobs row, and the client's only reaction was setError — which persists nothing. A
// dropped turn was therefore indistinguishable in the database from a turn the user never sent, which
// is why the reports behind CHATQA-2 could only be described as "silence".
//
// These go through the existing trackError channel (→ /api/analytics → analytics_events.is_error →
// the admin Activity "Errors" section), so they land next to the server's own reports for the same
// turn — matched on `turnId` from the stream's `meta` event. Message CONTENT is never sent, only
// sizes: enough to correlate with the server-side llm_jobs rows without copying user text into
// analytics.
export const CHAT_FAILED_EVENT = 'chat-turn-failed'
export const CHAT_STALLED_EVENT = 'chat-turn-stalled'
const CHAT_ERROR_TAG = 'chat-turn'

/** Identifies which conversation the turn belonged to and how big the request was. */
export interface ChatTurnContext {
  mode: 'onboarding' | 'project'
  projectId?: string | null
  /** Number of transcript messages sent on the wire this turn. */
  turnCount: number
  /** Total characters of transcript sent. The uncapped transcript is a direct cause of slow turns, so
   *  this is the number to correlate against when a project's chat starts failing consistently. */
  transcriptChars: number
}

function baseData(ctx: ChatTurnContext, stats: ChatTurnStats): Record<string, unknown> {
  return {
    side: 'client',
    mode: ctx.mode,
    projectId: ctx.projectId ?? null,
    turnId: stats.turnId,
    elapsedMs: stats.elapsedMs,
    bytesReceived: stats.bytesReceived,
    eventsReceived: stats.eventsReceived,
    lastEventKind: stats.lastEventKind,
    sawDone: stats.sawDone,
    sawText: stats.sawText,
    deltaChars: stats.deltaChars,
    sawUi: stats.sawUi,
    turnCount: ctx.turnCount,
    transcriptChars: ctx.transcriptChars,
  }
}

/** Report a failed turn. Safe to call with any thrown value; non-ChatStreamError throws are reported
 *  as 'network' so an unexpected client bug still produces a record instead of vanishing. */
export function reportChatFailure(err: unknown, ctx: ChatTurnContext): void {
  try {
    const known = err instanceof ChatStreamError ? err : null
    const reason: ChatFailureReason = known?.reason ?? 'network'
    const stats: ChatTurnStats = known?.stats ?? {
      turnId: null, elapsedMs: 0, bytesReceived: 0, eventsReceived: 0, lastEventKind: null,
      sawDone: false, sawText: false, deltaChars: 0, sawUi: false,
    }
    trackError(
      CHAT_FAILED_EVENT,
      {
        ...baseData(ctx, stats),
        reason,
        httpStatus: known?.httpStatus ?? null,
        message: (err instanceof Error ? err.message : String(err ?? 'unknown')).slice(0, 500),
      },
      CHAT_ERROR_TAG,
    )
  } catch {
    // Telemetry must never break the chat.
  }
}

/** Report a turn that has gone quiet but is still running — a warning, not a failure (see
 *  clientErrorSeverity in netlify/functions/lib/error-severity.ts). These are the leading indicator:
 *  a turn that stalls is a turn about to be killed by the execution limit. */
export function reportChatStall(stats: ChatTurnStats, ctx: ChatTurnContext): void {
  try {
    trackError(CHAT_STALLED_EVENT, { ...baseData(ctx, stats), reason: 'stalled' }, CHAT_ERROR_TAG)
  } catch {
    /* never break the chat */
  }
}

/** Whether the failed turn got as far as a durable row on the server.
 *
 *  `stats.turnId` is set by the start response, so this is false only when the POST that creates the turn
 *  never succeeded (an offline browser, a 401, a 5xx from the endpoint itself). Anything past that point —
 *  a lost network mid-poll, the client's own deadline, a turn the server reports as errored — is a turn the
 *  server knows about and will finish and record itself, so the transcript entry is not the browser's to
 *  write. See finalizeChatTurn in netlify/functions/lib/project-chat-turns.ts. */
export function turnExists(err: unknown): boolean {
  return err instanceof ChatStreamError && !!err.stats.turnId
}

/** What the user sees in the transcript when a turn is lost. Persisted as an assistant message so the
 *  stored conversation records the drop instead of showing an unanswered user turn — which also stops
 *  the next request from re-sending a run of consecutive user messages. */
// The strings live in CHAT_FAILURE_NOTICES (project-chat-transcript.ts) so the transcript cap can
// recognize and drop them on the way to the model — see isClientFailureNotice. Keep this mapping and
// that set as the SAME text: a notice this returns but the cap does not recognize would be replayed to
// the model as a prior assistant reply and derail the next turn.
export function chatFailureNotice(err: unknown): string {
  const reason = err instanceof ChatStreamError ? err.reason : 'network'
  // A 401 is permanent until the user signs in, so it must not get the "try again in a moment" copy —
  // that invited an infinite retry loop against an endpoint that could never answer.
  if (err instanceof ChatStreamError && err.httpStatus === 401) {
    return CHAT_FAILURE_NOTICES.expired
  }
  switch (reason) {
    case 'truncated':
      return CHAT_FAILURE_NOTICES.truncated
    case 'empty':
      return CHAT_FAILURE_NOTICES.empty
    case 'http':
    case 'network':
      return CHAT_FAILURE_NOTICES.unreachable
    default:
      return CHAT_FAILURE_NOTICES.generic
  }
}
